import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import ExportPdfTimeCommand from '../commands/hub/export.pdf.time.command';
import { BlockKeywordCommand } from '../commands/accounts/block.keyword.command';
import { CreateTourCommand, TourAttractionCommand } from '../commands/hub/create.tour.command';
import { AddEventBriteGeneralFeedCommand } from '../commands/hub/event/add.eventbrite.general.feed.command';
import { AddFacebookGeneralFeedCommand } from '../commands/hub/event/add.facebook.general.feed.command';
import { AddFacebookLocationFeedCommand } from '../commands/hub/event/add.facebook.location.feed.command';
import { CreateEventCommand } from '../commands/hub/event/create.event.command';
import { CreatePlaceCommand } from '../commands/hub/place/create.place.command';
import { SaveThemeCommand } from '../commands/hub/save.theme.command';
import { UpdateCityCommand } from '../commands/hub/update.city.command';
import { isNullOrUndefined } from '../common/extensions';
import { ApiService } from '../common/services/api.service';
import { HubCustomBtn } from '../models/hub/hub.custom.btn';
import { HubEvent } from '../models/hub/hub.event';
import { HubFeed } from '../models/hub/hub.feed';
import { HubPlace } from '../models/hub/hub.place';
import { HubTour } from '../models/hub/hub.tour';
import { HubVisitorDataSource } from '../models/hub/hub.visitor.data.source';
import { EventTimeService } from './event.time.service';
import { ShareService } from './share.service';
import {
  HubGooglePlaceBasicData,
  HubPlaceAdditionalData,
} from '../models/hub/hub.google.place.data';
import { isString } from 'util';
import { HubBlockedOrganizer } from '../models/hub/hub.blocked.organizer';
import { TravelBuddySettings } from '../models/hub/travel.buddy.settings';

@Injectable()
export class HubApiService {
  constructor(private apiService: ApiService) {}

  public getGlobalBlockedByKeywordsPlaces(hubName: string) {
    return this.apiService.get(`hubs/${hubName}/blocked-by-global-keywords`);
  }

  public getClientPlaceBlockKeywords(hubName: string) {
    return this.apiService.get(`hubs/${hubName}/client-place-block-keywords`);
  }

  public getClientPlaceBlockKeywordsPlaceList(hubName: string) {
    return this.apiService.get(`hubs/${hubName}/blocked-places-by-client-keywords`);
  }

  public addClientPlaceBlockKeyword(hubName: string, command: string[]): Observable<void> {
    return this.apiService.post(`hubs/${hubName}/client-place-block-keywords`, command);
  }

  public getBlockKeywords(hubName: string) {
    return this.apiService.get(`hubs/${hubName}/block-keywords`);
  }

  public deleteBlockKeyword(hubName: string, command: BlockKeywordCommand): Observable<void> {
    return this.apiService.delete(`hubs/${hubName}/block-keywords`, command);
  }

  public addBlockKeyword(hubName: string, command: BlockKeywordCommand): Observable<void> {
    return this.apiService.post(`hubs/${hubName}/block-keywords`, command);
  }

  public getBlockedEventDomains(hubName: string) {
    return this.apiService.get(`hubs/${hubName}/block-event-domains`);
  }

  public updateBlockedEventDomains(hubName: string, command: string[]): Observable<void> {
    return this.apiService.post(`hubs/${hubName}/block-event-domains`, command);
  }

  public getAISettings(hubName: string): Observable<{ travelBuddy: TravelBuddySettings }> {
    return this.apiService.get(`hubs/${hubName}/ai-settings`);
  }

  public setTravelBuddyUI(hubName: string, ui: any) {
    return this.apiService.put(`hubs/${hubName}/travel-buddy-ui`, ui);
  }

  public setTravelBuddyIndicator(hubName: string, indicator: any) {
    return this.apiService.put(`hubs/${hubName}/travel-buddy-indicator`, indicator);
  }

  public getTravelBuddyClientUploadedFiles(hubName: string) {
    return this.apiService.get(`hubs/${hubName}/travel-buddy-client-uploaded-files`);
  }

  public uploadTravelBuddyClientFiles(hubName: string, files: File[]) {
    let fd = new FormData();
    for (const file of files) {
      fd.append('files', file);
    }
    return this.apiService.post(`hubs/${hubName}/travel-buddy-upload-client-files`, fd);
  }

  public deleteTravelBuddyClientFile(hubName: string, fileId: string) {
    return this.apiService.delete(`hubs/${hubName}/travel-buddy-client-uploaded-file`, { fileId });
  }

  public extractContentFromLink(hubName: string, link: string, isIncludeLinks = false) {
    return this.apiService
      .post(`hubs/${hubName}/extract-content-from-link`, { link, isIncludeLinks })
      .toPromise();
  }

  public uploadLinkContent(hubName: string, link: string, content: string) {
    return this.apiService
      .post(`hubs/${hubName}/travel-buddy-save-link-content`, { link, content })
      .toPromise();
  }

  public getThemeSetting(hubName: string) {
    return this.apiService.get(`hubs/${hubName}/theme`);
  }

  public saveTheme(hubName: string, command: SaveThemeCommand): Observable<void> {
    return this.apiService.put(`hubs/${hubName}/theme`, command);
  }

  public getEvents(hubName, paramObj): Observable<HubEvent[]> {
    return this.apiService.get(`hubs/${hubName}/events`, paramObj).map((data) => {
      data.forEach((item) => {
        EventTimeService.processEventTime(item);
        // to fix the Facebook event ticket URL contains 'lwww' issue, which was caused by the replacement of 'm.facebook' to 'www.facebook', but the initial URL is 'lm.facebook',
        // then remove the prefix to avoid jump to facebook firstly
        if (item.ticketUri && item.ticketUri.startsWith('http')) {
          let url = item.ticketUri.replace('lwww', 'lm');
          url = url.replace('https://lm.facebook.com/l.php?u=', '');
          url = decodeURIComponent(url);
          url = url.split('fbclid')[0];
          url = url.endsWith('?') ? url.substr(0, url.length - 1) : url;
          item.ticketUri = url;
        }
        //DefaultImageWebSideService.getDefaultImageByKeyword(item);
      });
      return data;
    });
  }

  public getPlaces(hubName, paramObj): Observable<HubPlace[]> {
    return this.apiService.get(`hubs/${hubName}/places`, paramObj).map((data) => {
      data.forEach((item) => {
        if (!isNullOrUndefined(item.lat) && !isNullOrUndefined(item.lng)) {
          let address = item.address;
          item.address = {
            address,
            lat: item.lat,
            lng: item.lng,
          };
        }
      });
      return data;
    });
  }

  public getNoDescriptionPlaces(hubName): Observable<HubPlace[]> {
    return this.apiService.get(`hubs/${hubName}/places/no-description`);
  }

  public savePlacesDescriptions(hubName, placesWithDescription): Observable<HubPlace[]> {
    return this.apiService.post(`hubs/${hubName}/places/save-description`, {
      placesWithDescription,
    });
  }

  public recordExportPdfTime(hubName, paramObj: ExportPdfTimeCommand): Observable<any> {
    return this.apiService.post(`hubs/${hubName}/record-export-pdf-time`, paramObj);
  }

  public getGooglePlaces(hubName, paramObj): Observable<HubPlace[]> {
    return this.apiService.get(`hubs/${hubName}/places/google-places`, paramObj).map((data) => {
      data.forEach((item) => {
        if (!isNullOrUndefined(item.lat) && !isNullOrUndefined(item.lng)) {
          item.address = {
            address: item.address,
            lat: item.lat,
            lng: item.lng,
          };
        }
      });
      return data;
    });
  }

  public getPlacesByPlaceIds(hubName, placeIds: string[]): Observable<HubPlace[]> {
    return this.apiService.get(`hubs/${hubName}/places/placeIds`, { placeIds: placeIds });
  }

  public getBlockPlaces(hubName, paramObj): Observable<HubPlace[]> {
    return this.apiService.get(`hubs/${hubName}/places/block`, paramObj);
  }

  public getBlockEvents(hubName, paramObj): Observable<HubEvent[]> {
    return this.apiService.get(`hubs/${hubName}/events/block`, paramObj);
  }

  public getBlockedOrganizers(hubName: string): Observable<HubBlockedOrganizer[]> {
    return this.apiService.get(`hubs/${hubName}/blocked-organizers`);
  }

  public removePageFeed(hubName, feedId): Observable<void> {
    return this.apiService.delete(`hubs/${hubName}/pages-feed/${feedId}`);
  }

  public addFacebookPageFeed(
    hubName: string,
    command: AddFacebookGeneralFeedCommand
  ): Observable<any> {
    return this.apiService.post(`hubs/${hubName}/page-feeds`, command, { timeout: 1000 * 1000 });
  }

  public addEventbriteOrganizerFeed(
    hubName: string,
    command: AddEventBriteGeneralFeedCommand
  ): Observable<any> {
    return this.apiService.post(`hubs/${hubName}/eventbrite/organizer`, command, {
      timeout: 1000 * 1000,
    });
  }

  public addEventfulVenueFeed(
    hubName: string,
    command: AddEventBriteGeneralFeedCommand
  ): Observable<any> {
    return this.apiService.post(`hubs/${hubName}/eventful/venue`, command, {
      timeout: 1000 * 1000,
    });
  }

  public getPageFeeds(hubName: string): Observable<HubFeed[]> {
    return this.apiService.get(`hubs/${hubName}/page-feeds`);
  }

  public getLocationFeeds(hubName: string): Observable<HubFeed[]> {
    return this.apiService.get(`hubs/${hubName}/location-feeds`);
  }

  public getMultipleCitiesToggle(hubName: string): Observable<any> {
    return this.apiService.get(`hubs/${hubName}/multiple-cities-toggle`);
  }

  public toggleMultipleCities(hubName: string, status: boolean): Observable<any> {
    return this.apiService.put(`hubs/${hubName}/multiple-cities-toggle`, { status });
  }

  public toggleCityBlock(hubName: string, status: boolean): Observable<any> {
    return this.apiService.put(`hubs/${hubName}/multiple-cities-block`, { active: status });
  }

  public getCityBlockToggle(hubName: string): Observable<any> {
    return this.apiService.get(`hubs/${hubName}/multiple-cities-block`);
  }

  public getRegions(hubName: string): Observable<any> {
    return this.apiService.get(`hubs/${hubName}/regions`);
  }

  public deleteRegion(hubName: string, id: string): Observable<any> {
    return this.apiService.delete(`hubs/${hubName}/region?id=${id}`);
  }

  public addCitiesToRegion(hubName: string, regionId: string, cityIds: string[]): Observable<any> {
    return this.apiService.put(`hubs/${hubName}/add-cities-to-region`, { id: regionId, cityIds });
  }

  public removeCityFromRegion(hubName: string, regionId: string, cityId: string): Observable<any> {
    return this.apiService.put(`hubs/${hubName}/remove-city-from-region`, { id: regionId, cityId });
  }

  public assignTourToRegions(
    hubName: string,
    tourId: string,
    regionIds: string[]
  ): Observable<any> {
    return this.apiService.put(`hubs/${hubName}/assign-tour-to-regions`, { tourId, regionIds });
  }

  public createRegion(hubName: string, command: any): Observable<any> {
    return this.apiService.post(`hubs/${hubName}/region`, command);
  }

  public updateRegionName(hubName: string, command: any): Observable<any> {
    return this.apiService.put(`hubs/${hubName}/region`, command);
  }

  public updateCity(hubName: string, command: UpdateCityCommand): Observable<any> {
    let formData = new FormData();

    for (let property of Object.keys(command)) {
      if (command[property]) {
        formData.append(property, command[property]);
      }
    }
    formData.set('id', command.id);
    formData.set('cityTitle', command.cityTitle);
    if (!!command.image) {
      formData.set('image', command.image, command.image.name);
    } else {
      formData.set('image', null);
    }

    return this.apiService.post(`hubs/${hubName}/city`, formData);
  }

  public updateFBSpiderEvent(hubName: string, link: string): Observable<any> {
    return this.apiService.put(`hubs/${hubName}/fb-spider-event`, { hubName, link });
  }

  public getAllSettings(hubName: string): Observable<any> {
    return this.apiService.get(`hubs/${hubName}/all-backend-settings`);
  }

  public getPlaceCategoryToggle(hubName: string): Observable<any> {
    return this.apiService.get(`hubs/${hubName}/place-category-toggle`);
  }

  public getLatLngByAddress(hubName, placeName): Observable<any> {
    return this.apiService.get(`hubs/${hubName}/geo-location?placeName=${placeName}`);
  }

  public getTimezoneByLatlng(lat, lng): Observable<any> {
    return this.apiService.get(`common/timezone?lat=${lat}&lng=${lng}`);
  }

  public getDefaultTimezoneFormatter(hubName: string): Observable<string> {
    return this.apiService.get(`hubs/${hubName}/default-timezone-formatter`);
  }

  public getPulledGooglePlaces(): Observable<any> {
    return this.apiService.get(`common/pulled-google-places`);
  }

  public getPlainFBPlaces(hubName): Observable<any> {
    return this.apiService.get(`common/plain-fb-places?hubName=${hubName}`);
  }

  public getFBPosts(fbId): Observable<any> {
    return this.apiService.get(`common/fb-posts?fbId=${fbId}`);
  }

  // public getGooglePlaceDetails(placeId): Observable<any> {
  //     return this.apiService.get(`common/google-place-details?placeId=` + placeId);
  // }

  public getGooglePlaceDetails(
    hubName,
    placeId,
    waitForUploading = false
  ): Observable<HubPlaceAdditionalData> {
    return this.apiService.get(
      `visitors/${hubName}/places/google-place-details?placeId=${placeId}&waitForUploading=${waitForUploading}`
    );
  }

  public searchGooglePlaceByName(hubName, placeName): Observable<any> {
    return this.apiService.get(
      `hubs/${hubName}/places/search-google-place-by-name?placeName=${placeName}`
    );
  }

  public saveGooglePlace(
    hubName,
    isApproved: boolean,
    place: HubGooglePlaceBasicData
  ): Observable<any> {
    let formData = new FormData();

    for (let property of Object.keys(place)) {
      if (place[property]) {
        formData.append(
          property,
          isString(place[property]) ? place[property] : JSON.stringify(place[property])
        );
      }
    }

    if (!!place.uploadedImage) {
      formData.set('image', place.uploadedImage, place.uploadedImage.name);
    } else {
      formData.set('image', null);
    }

    formData.set('isApproved', isApproved.toString());

    return this.apiService.post(`hubs/${hubName}/places/save-google-place`, formData);
  }

  public getDataSource(hubName: string): Observable<HubVisitorDataSource> {
    return this.apiService.get(`hubs/${hubName}/data-source`);
  }

  public setPlaceSource(hubName: string, source: string, googleKey: string): Observable<any> {
    return this.apiService.put(`hubs/${hubName}/place-source`, { source, googleKey });
  }

  public getLocationStatus(hubName, lat, lng, socialName): Observable<any> {
    return this.apiService.get(
      `hubs/${hubName}/location-status?lat=${lat}&lng=${lng}&socialName=${socialName}`
    );
  }

  public addFacebookLocationFeed(
    hubName,
    command: AddFacebookLocationFeedCommand
  ): Observable<void> {
    return this.apiService.post(`hubs/${hubName}/location-feeds`, command, {
      timeout: 1200 * 1000,
    });
  }

  public addGoogleLocationFeed(hubName, command: AddFacebookLocationFeedCommand): Observable<any> {
    return this.apiService.post(`hubs/${hubName}/google-location-feeds`, command, {
      timeout: 1200 * 1000,
    });
  }

  public updateGoogleLocationFeed(
    hubName,
    command: {
      id: string;
      categories: string[];
      radius: number;
      isRepullExistingCategories: boolean;
      points: any;
      filterByCity: string;
    }
  ): Observable<any> {
    return this.apiService.put(`hubs/${hubName}/google-location-feeds`, command, {
      timeout: 1200 * 1000,
    });
  }

  public getGooglePlaceBlockedCategories(hubName): Observable<any> {
    return this.apiService.get(`hubs/${hubName}/google-place-blocked-categories`);
  }

  public getAllowedPlaceCategories(hubName: string): Observable<string[]> {
    return this.apiService.get(`hubs/${hubName}/places/allowed-place-categories`);
  }

  public checkPullingGooglePlaceStatus(hubName): Observable<any> {
    return this.apiService.get(`hubs/${hubName}/check-google-places-pulling-status`);
  }

  public addEventBriteLocationFeed(
    hubName,
    command: AddFacebookLocationFeedCommand
  ): Observable<void> {
    return this.apiService.post(`hubs/${hubName}/eventbrite/location`, command, {
      timeout: 500 * 1000,
    });
  }

  public addEventfulLocationFeed(
    hubName,
    command: AddFacebookLocationFeedCommand
  ): Observable<void> {
    return this.apiService.post(`hubs/${hubName}/eventful/location`, command, {
      timeout: 500 * 1000,
    });
  }

  public removeLocationFeed(hubName, feedId, socialName): Observable<void> {
    return this.apiService.delete(`hubs/${hubName}/location-feed/${feedId}/${socialName}`);
  }

  public approveEvent(hubName, eventId) {
    return this.apiService.put(`hubs/${hubName}/events/${eventId}/approve`);
  }

  public unApproveEvent(hubName, eventId) {
    return this.apiService.put(`hubs/${hubName}/events/${eventId}/un-approve`);
  }

  public pinEvent(hubName, eventId) {
    return this.apiService.put(`hubs/${hubName}/events/${eventId}/pin`);
  }

  public unPinEvent(hubName, eventId) {
    return this.apiService.put(`hubs/${hubName}/events/${eventId}/un-pin`);
  }

  public choiceEvent(hubName, eventId) {
    return this.apiService.put(`hubs/${hubName}/events/${eventId}/choice`);
  }

  public customEvent(hubName, eventId) {
    return this.apiService.put(`hubs/${hubName}/events/${eventId}/custom`);
  }

  public unChoiceEvent(hubName, eventId) {
    return this.apiService.put(`hubs/${hubName}/events/${eventId}/un-choice`);
  }

  public unCustomEvent(hubName, eventId) {
    return this.apiService.put(`hubs/${hubName}/events/${eventId}/un-custom`);
  }

  public removeEvent(hubName, eventId) {
    return this.apiService.put(`hubs/${hubName}/events/${eventId}/remove`);
  }

  public unRemoveEvent(hubName, eventId) {
    return this.apiService.put(`hubs/${hubName}/events/${eventId}/un-remove`);
  }

  public unBlockOrganizer(hubName, organizer: HubBlockedOrganizer) {
    return this.apiService.put(`hubs/${hubName}/unblocked-organizer`, { organizer });
  }

  public deleteEvent(hubName, eventId) {
    return this.apiService.put(`hubs/${hubName}/events/${eventId}/delete-permanently`);
  }

  public block(hubName, eventId) {
    return this.apiService.put(`hubs/${hubName}/events/${eventId}/block`);
  }

  public unBlock(hubName, eventId) {
    return this.apiService.put(`hubs/${hubName}/events/${eventId}/un-block`);
  }

  public lock(hubName, eventId) {
    return this.apiService.put(`hubs/${hubName}/events/${eventId}/lock`);
  }

  public unlock(hubName, eventId) {
    return this.apiService.put(`hubs/${hubName}/events/${eventId}/unlock`);
  }

  public getSimilarPlaces(hubName, group): Observable<any> {
    return this.apiService.get(`hubs/${hubName}/similar-places?group=${group}`);
  }

  public getSimilarEvents(hubName): Observable<{
    similarEventsByExactSame: {
      group: HubEvent[];
      condition: any[];
    }[];
    similarEventsBy2ParamSimilarity: {
      group: HubEvent[];
      condition: any[];
    }[];
    similarMultiDatesEvents: {
      group: HubEvent[];
      condition: any[];
    }[];
  }> {
    return this.apiService.get(`hubs/${hubName}/similar`).map((data) => {
      data.similarEventsByExactSame.forEach((x) =>
        x.group.forEach((item) => {
          EventTimeService.processEventTime(item);
        })
      );
      data.similarMultiDatesEvents.forEach((x) =>
        x.group.forEach((item) => {
          EventTimeService.processEventTime(item);
        })
      );
      return {
        similarEventsByExactSame: data.similarEventsByExactSame,
        similarEventsBy2ParamSimilarity: data.similarEventsBy2ParamSimilarity,
        similarMultiDatesEvents: data.similarMultiDatesEvents,
      };
    });
  }

  public getSpiderEventPages(hubName) {
    return this.apiService.get(`hubs/${hubName}/spider-event-pages`);
  }

  public getPlaceLocationFilters(hubName) {
    return this.apiService.get(`visitors/${hubName}/places/location-filter`);
  }

  public getEventLocationFilters(hubName) {
    return this.apiService.get(`visitors/${hubName}/events/location-filter`);
  }

  public saveSpiderEventPages(hubName, url) {
    return this.apiService.post(`hubs/${hubName}/spider-event-pages`, { url });
  }

  public deleteSpiderEventPage(hubName, page) {
    return this.apiService.put(`hubs/${hubName}/delete-spider-event-page`, { page });
  }

  public editSpiderEventPage(hubName, page, newUrl) {
    return this.apiService.put(`hubs/${hubName}/edit-spider-event-page`, { page, newUrl });
  }

  public saveCsvEvents(hubName, events, isApproved) {
    return this.apiService.post(`hubs/${hubName}/save-csv-events`, { events, isApproved });
  }

  public approvePlace(hubName, placeId) {
    return this.apiService.put(`hubs/${hubName}/places/${placeId}/approve`);
  }

  public unApprovePlace(hubName, placeId) {
    return this.apiService.put(`hubs/${hubName}/places/${placeId}/un-approve`);
  }

  public getPlaceById(hubName, placeId) {
    return this.apiService.get(`hubs/${hubName}/places/${placeId}`);
  }

  public pinPlace(hubName, placeId) {
    return this.apiService.put(`hubs/${hubName}/places/${placeId}/pin`);
  }

  public unPinPlace(hubName, placeId) {
    return this.apiService.put(`hubs/${hubName}/places/${placeId}/un-pin`);
  }

  public choicePlace(hubName, placeId) {
    return this.apiService.put(`hubs/${hubName}/places/${placeId}/choice`);
  }

  public unChoicePlace(hubName, placeId) {
    return this.apiService.put(`hubs/${hubName}/places/${placeId}/un-choice`);
  }

  public customPlace(hubName, placeId) {
    return this.apiService.put(`hubs/${hubName}/places/${placeId}/custom`);
  }

  public unCustomPlace(hubName, placeId) {
    return this.apiService.put(`hubs/${hubName}/places/${placeId}/un-custom`);
  }

  public toBeExportPlace(hubName, eventId) {
    return this.apiService.put(`hubs/${hubName}/places/${eventId}/export`);
  }

  public unToBeExportPlace(hubName, placeId) {
    return this.apiService.put(`hubs/${hubName}/places/${placeId}/un-export`);
  }

  public uploadPlaceImage(hubName, placeId, command) {
    let formData = new FormData();
    if (!!command.image) {
      formData.set('image', command.image, command.image.name);
    } else {
      formData.set('image', null);
    }
    return this.apiService.put(`hubs/${hubName}/places/${placeId}/image`, formData);
  }

  public updatePlaceImage(hubName, placeId, imageLink) {
    return this.apiService.put(`hubs/${hubName}/places/${placeId}/image`, { imageLink });
  }

  public updatePlace(hubName, placeId, place: HubPlace) {
    let formData = new FormData();

    for (let property of Object.keys(place)) {
      if (place[property]) {
        formData.append(property, place[property]);
      }
    }
    formData.set('address', JSON.stringify(place.address));
    if (!!place.imageFile) {
      formData.set('image', place.imageFile, place.imageFile.name);
    } else {
      formData.set('image', null);
    }
    formData.set('lock', JSON.stringify(place.lock));
    formData.set('isApproved', place.isApproved.toString());
    formData.set('audio', JSON.stringify(place.audio));
    return this.apiService.put(`hubs/${hubName}/places/${placeId}/`, formData);
  }

  public updateEvent(hubName, eventId, command: any) {
    let formData = new FormData();

    for (let property of Object.keys(command)) {
      if (command[property]) {
        formData.append(property, command[property]);
      }
    }
    formData.set('owner', JSON.stringify(command.owner));
    formData.set('eventTimes', JSON.stringify(command.eventTimes));
    formData.set('eventTimesText', JSON.stringify(command.eventTimesText));
    formData.set('address', JSON.stringify(command.address));
    formData.set('ticketInfo', JSON.stringify(command.ticketInfo));
    formData.set('cover', JSON.stringify(command.cover));
    if (!!command.imageFile) {
      formData.set('image', command.imageFile, command.imageFile.name);
    } else {
      formData.set('image', null);
    }
    return this.apiService.put(`hubs/${hubName}/events/${eventId}/`, formData);
  }

  public toBeExportEvent(hubName, eventId) {
    return this.apiService.put(`hubs/${hubName}/events/${eventId}/export`);
  }

  public unToBeExportEvent(hubName, eventId) {
    return this.apiService.put(`hubs/${hubName}/events/${eventId}/un-export`);
  }

  public blockPlace(hubName, placeId) {
    return this.apiService.put(`hubs/${hubName}/places/${placeId}/block`);
  }

  public unBlockPlace(hubName, placeId) {
    return this.apiService.put(`hubs/${hubName}/places/${placeId}/un-block`);
  }

  public deletePlace(hubName, placeId) {
    return this.apiService.put(`hubs/${hubName}/places/${placeId}/delete-permanently`);
  }

  public deleteCustomEventBtn(hubName, id): Observable<HubCustomBtn> {
    return this.apiService.delete(`hubs/${hubName}/custom-event/${id}`);
  }

  public deleteCustomPlaceBtn(hubName, id): Observable<HubCustomBtn> {
    return this.apiService.delete(`hubs/${hubName}/custom-place/${id}`);
  }

  public bulkClassifyEventCategories(
    hubName,
    events: { _id: string; name: string; categories?: { _id: string; name: string }[] }[]
  ): Observable<HubCustomBtn> {
    return this.apiService.put(`hubs/${hubName}/bulk-add-to-custom-event`, events);
  }

  public transferPlaces(hubName, id): Observable<HubCustomBtn> {
    return this.apiService.put(`hubs/${hubName}/transfer-places/${id}`);
  }

  public addEventToCustomBtn(
    hubName: string,
    eventId: string,
    btnIndex: number
  ): Observable<any[]> {
    return this.apiService.put(`hubs/${hubName}/events/${eventId}/add-to-custom-event`, {
      btnIndex,
    });
  }

  public removeEventFromCustomBtn(
    hubName: string,
    eventId: string,
    btnIndex: number
  ): Observable<any[]> {
    return this.apiService.put(`hubs/${hubName}/events/${eventId}/remove-from-custom-event`, {
      btnIndex,
    });
  }

  public addPlaceToCustomBtn(
    hubName: string,
    placeId: string,
    btnIndex: number
  ): Observable<any[]> {
    return this.apiService.put(`hubs/${hubName}/places/${placeId}/add-to-custom-place`, {
      btnIndex,
    });
  }

  public removePlaceFromCustomBtn(
    hubName: string,
    placeId: string,
    btnIndex: number
  ): Observable<any[]> {
    return this.apiService.put(`hubs/${hubName}/places/${placeId}/remove-from-custom-place`, {
      btnIndex,
    });
  }

  public createEvent(hubName, command: CreateEventCommand, frequencyEventTimes: any[]) {
    let formData = new FormData();

    for (let property of Object.keys(command)) {
      if (command[property]) {
        formData.append(property, command[property]);
      }
    }
    if (frequencyEventTimes && frequencyEventTimes.length) {
      formData.set('eventTimes', JSON.stringify(frequencyEventTimes));
    } else {
      formData.set('eventTimes', JSON.stringify(command.eventTimes));
    }
    formData.set('eventTimesText', JSON.stringify(command.eventTimesText));
    formData.set('address', JSON.stringify(command.address));
    if (!!command.image) {
      formData.set('image', command.image, command.image.name);
    } else {
      formData.set('image', null);
    }

    return this.apiService.post(`hubs/${hubName}/events`, formData);
  }

  public createPlace(hubName, command: CreatePlaceCommand) {
    let formData = new FormData();

    for (let property of Object.keys(command)) {
      if (command[property]) {
        formData.append(property, command[property]);
      }
    }
    formData.set('address', JSON.stringify(command.address));
    if (!!command.image) {
      formData.set('image', command.image, command.image.name);
    } else {
      formData.set('image', null);
    }

    return this.apiService.post(`hubs/${hubName}/places`, formData);
    // return Observable.of(list);
  }

  public getTours(hubName: string): Observable<any[]> {
    return this.apiService.get(`hubs/${hubName}/tours`);
  }

  public updateTours(hubName: string, tours: any): Observable<any[]> {
    return this.apiService.put(`hubs/${hubName}/tours`, tours);
  }

  public getTourDetail(hubName: string, tourId, isForVisitor = false): Observable<HubTour> {
    return this.apiService.get(`hubs/${hubName}/tours/${tourId}/${isForVisitor}`).map((y) => {
      let list = [];
      y.attractions.forEach((x) => {
        if (x.status.toLowerCase() === 'ok') {
          x.startTime && (x.item = EventTimeService.processEventTime(x.item));
          if (!isNullOrUndefined(x.item.lat) && !isNullOrUndefined(x.item.lng)) {
            x.item.address = {
              address: x.item.address,
              lat: x.item.lat,
              lng: x.item.lng,
            };
          }
          list.push(x);
        }
      });
      y.attractions = list;
      return y;
    });
  }

  public addAttractionToTour(hubName: string, item: any): Observable<any[]> {
    //return this.apiService.put(`hubs/${hubName}/tours/add-attraction`, item);
    return this.apiService.put(`hubs/${hubName}/tours/add-attraction-v3`, item);
  }

  public removeAttractionFromTour(hubName: string, item: any): Observable<any[]> {
    //return this.apiService.put(`hubs/${hubName}/tours/delete-attraction`, item);
    return this.apiService.put(`hubs/${hubName}/tours/delete-attraction-v3`, item);
  }

  public updateTourAttractions(hubName: string, command: any) {
    let cmd = new TourAttractionCommand();
    cmd.id = command.id;
    cmd.attractions = [];
    command.attraction.forEach((x) => {
      cmd.attractions.push({
        feedId: x.feedId,
        feedType: x.feedType,
        order: x.order,
      });
    });
    return this.apiService.put(`hubs/${hubName}/tours/update-attraction`, cmd);
  }

  public updateTour(hubName: string, tourId, command: CreateTourCommand) {
    let formData = this.getTourFormData(command);
    return this.apiService.put(`hubs/${hubName}/tours/update/${tourId}`, formData);
  }

  public updateTourTravelTimeAndDistance(
    hubName: string,
    tourId,
    time: number,
    distance: number,
    stops: number
  ) {
    return this.apiService.put(`hubs/${hubName}/tours/update-travel-time-distance/${tourId}`, {
      travelTime: time,
      distance: distance,
      stops: stops,
    });
  }

  public updateTourTitleAndDescription(
    hubName: string,
    tourId,
    title: string,
    description: string
  ) {
    return this.apiService.put(`hubs/${hubName}/tours/update-title-description/${tourId}`, {
      title: title,
      description: description,
    });
  }

  public updateTourImage(hubName: string, tourId, command: CreateTourCommand) {
    let formData = this.getTourFormData(command);
    return this.apiService.put(`hubs/${hubName}/tours/update-image/${tourId}`, formData);
  }

  public updateTourImageByBase64(hubName: string, tourId, base64: string) {
    return this.apiService.put(`hubs/${hubName}/tours/update-image-base64/${tourId}`, { base64 });
  }

  public createTour(hubName, command: CreateTourCommand) {
    let formData = this.getTourFormData(command);
    return this.apiService.post(`hubs/${hubName}/tours`, formData);
  }

  public createTourWithBase64Image(hubName, command: CreateTourCommand) {
    return this.apiService.post(`hubs/${hubName}/tours/tours-image-base64`, command);
  }

  public publishTour(hubName, id: any) {
    return this.apiService.put(`hubs/${hubName}/tours/publish/${id}`);
  }

  public dePublishTour(hubName, id: any) {
    return this.apiService.put(`hubs/${hubName}/tours/de-publish/${id}`);
  }

  public deleteTour(hubName, id: any) {
    return this.apiService.delete(`hubs/${hubName}/tours/${id}`);
  }

  public updateFBImage(link: string, _id: string, name: string) {
    if (link && link.indexOf('https://www.facebook.com/pages/') >= 0) {
      return Observable.of('is_owned = false');
    }
    let id = ShareService.getFacebookPlaceId(link);
    return this.apiService.get(`common/update-fb-image?id=${id}&_id=${_id}&name=${name}`);
  }

  public uploadBase64ImageToAWS(
    imgBase64: string,
    maxWidth?: number,
    album?: string
  ): Promise<{ success: boolean; url: string }> {
    return this.apiService
      .post(`common/upload-img-to-aws`, { imgBase64, maxWidth, album })
      .toPromise();
  }

  public refreshEventImage(hubName, id) {
    return this.apiService.put(`hubs/${hubName}/events/${id}/refresh-image`);
  }

  public aiAssistant(hubName, query) {
    return this.apiService.post(`visitors/${hubName}/ai-assistant`, query);
  }

  public getRegionalAccountDMOs(hubName: string): Observable<string[]> {
    return this.apiService.get(`hubs/${hubName}/regional-account-dmos`);
  }

  private getTourFormData(command: CreateTourCommand) {
    let formData = new FormData();

    for (let property of Object.keys(command)) {
      if (command[property]) {
        formData.append(property, command[property]);
      }
    }

    if (!!command.image) {
      formData.set('image', command.image, command.image.name);
    } else {
      formData.set('image', null);
    }
    return formData;
  }

  public resizeImageFromUnApprovedEvent(
    hubName: string,
    eventId: string
  ): Observable<{ success: boolean; url: string; error: any }> {
    return this.apiService.put(`hubs/${hubName}/events/${eventId}/enlarge-img`);
  }
}



// WEBPACK FOOTER //
// D:/Work/Development/vsts_build/_work/2/s/web/src/services/hub.api.service.ts